import { useState } from 'react';
import type { FormEvent } from 'react';

import { CLICK_IDS } from '@sunflower-games/analytics-events';
import type { TFunction } from 'i18next';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import Button from '../../../../components/buttons/Button';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import { FormInput } from '../../../../components/FormInput/FormInput';
import type { ErrorMessageMap } from '../../../../components/FormInput/types';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { analytics } from '../../../../services/analytics';
import { useStores } from '../../../../stores/useStores';
import { getErrorMessage } from '../../../../utils/errors';

import classes from './ForgotPasswordSection.module.scss';

const emailErrorMessageMap = (t: TFunction): ErrorMessageMap => ({
  typeMismatch: t('validation.emailInvalid'),
  valueMissing: t('validation.emailRequired'),
});

interface ForgotPasswordForm extends HTMLFormElement {
  elements: HTMLFormControlsCollection & {
    email: HTMLInputElement;
  };
}

interface ForgotPasswordSectionProps extends WizardChildProps {
  onPasswordResetSuccess?: () => void;
}

export const ForgotPasswordSection = observer(
  ({ nextStep, forceStep, onPasswordResetSuccess }: ForgotPasswordSectionProps) => {
    const { t } = useTranslation();
    const { authStore } = useStores();

    const [error, setError] = useState<string>('');
    const [email, setEmail] = useState('');
    const isProcessing = computed(() => authStore.pending('sendPasswordResetLink')).get();

    const landingKey = authStore.getLandingKey();

    const clearError = () => setError('');

    const handleSubmit = async (event: FormEvent<ForgotPasswordForm>) => {
      event.preventDefault();
      const { email: elEmail } = event.currentTarget.elements;

      if (!elEmail.value.trim()) {
        setError(t('landing.direct.form.emailAddressRequired'));
        return;
      }

      try {
        void analytics.sendEvent('log_in', {
          step_name: 'forgot_password',
          event_action: 'click',
          click_id: CLICK_IDS.AUTH_FLOW.START_RESET_PASSWORD,
          landingKey,
        });

        await authStore.sendPasswordResetLink(elEmail.value);

        if (nextStep) {
          nextStep();
        } else if (onPasswordResetSuccess) {
          onPasswordResetSuccess();
        }
      } catch (e) {
        const errorMessage = getErrorMessage(e);
        console.error(`ForgotPasswordSection: Password reset failed: ${errorMessage}`);
        setError(errorMessage);
      }
    };

    const handleBackToSignUp = () => {
      if (forceStep) {
        forceStep(0, 'signup');
      }
    };

    return (
      <div className={classes.forgotPasswordSection}>
        <div className={classes.headerSection}>
          <h2 className={classes.title}>{t('auth.resetPassword')}</h2>
          <p className={classes.subtitle}>{t('auth.enterEmailInstructions')}</p>
        </div>

        <form className={classes.emailFormWrapper} onSubmit={handleSubmit}>
          <div className={classes.formRow}>
            <FormInput
              required
              autoComplete="email"
              disabled={isProcessing}
              errorMessageMap={emailErrorMessageMap(t)}
              id="email"
              name="email"
              placeholder={t('forms.enterEmailAddress')}
              size="sm"
              type="email"
              value={email}
              onChange={(event) => {
                clearError();
                setEmail(event.target.value);
              }}
            />
          </div>

          <ErrorMessage noSpaceWhenEmpty className={classes.authErrorMessage}>
            {error}
          </ErrorMessage>

          <Button btnType="main" className={classes.resetPasswordButton} disabled={isProcessing} type="submit">
            {isProcessing ? t('common.sending') : t('auth.resetPasswordUpperCase')}
          </Button>

          <div className={classes.backToLoginLink} data-testid="direct-forgot-password-signup-line">
            <Trans
              components={{
                question: <span />,
                signup: <span className={classes.backToLoginText} onClick={handleBackToSignUp} />,
              }}
              i18nKey="landing.direct.dontHaveAccountSignUp"
            />
          </div>
        </form>
      </div>
    );
  },
);
